import { createReactContext, createReactDispatchContext } from "../../../utils/reactContext";
import { DetailedPaymentPrefUnionObjectType } from "../../PaymentPrefConsolidated/common/types";
import { PayeeVendorTaxDocument } from "../PayeeVendorWorkflow/types";
import { PayeeBaseAddress, PayeeBaseSupportingDocument } from "../types";
import { PayeeActionTypes, PayeeStepActionTypes, PayeeWorkflowModes } from "./constants";

export type PayeeData = {
  arenaId?: string;
  arenaName?: string;
  payeeName?: string;
  paymentPrefId?: number;
  supportingDocuments?: PayeeBaseSupportingDocument[];

  //Vendor Specific Items
  payeeAddress?: PayeeBaseAddress;
  taxCountryId?: string;
  taxId?: string;
  // form1099Required is a string because it is a radio button value and radio button form components in the
  // design system only support strings and numbers.
  form1099Required?: string;
  invoices?: PayeeBaseSupportingDocument[];
  taxDocuments?: PayeeVendorTaxDocument[];

  paymentPref?: DetailedPaymentPrefUnionObjectType;

  // only used in edit mode to determine which payee to update
  payeeId?: string;
};

export const initialPayeeState: PayeeData = {
  arenaId: undefined,
  arenaName: undefined,
  payeeName: undefined,
  payeeAddress: undefined,
  paymentPrefId: undefined,
  supportingDocuments: undefined,
  taxCountryId: undefined,
  taxId: undefined,
  form1099Required: undefined,
  invoices: undefined,
  taxDocuments: [],
  payeeId: undefined,
};

export type PayeeReducer = {
  payeeData: PayeeData;
  action: PayeeReducerAction;
};

export type PayeeReducerAction = {
  type: PayeeActionTypes;
  payload: Partial<PayeeData>;
};

export const payeeReducer = (state: PayeeData, action: PayeeReducerAction): PayeeData => {
  switch (action.type) {
    case PayeeActionTypes.SET_PAYEE_DETAILS_ENTITY_AND_INVESTMENT: {
      const payeeName = action.payload.payeeName || state.payeeName;
      return {
        ...state,
        payeeName,
      };
    }
    case PayeeActionTypes.SET_PAYEE_DETAILS_VENDOR: {
      return {
        ...state,
        payeeName: action.payload.payeeName,
        taxId: action.payload.taxId,
        taxCountryId: action.payload.taxCountryId,
        form1099Required: action.payload.form1099Required,
        payeeAddress: action.payload.payeeAddress,
      };
    }
    case PayeeActionTypes.SET_INVOICES: {
      return {
        ...state,
        invoices: action.payload.invoices,
      };
    }
    case PayeeActionTypes.SET_SUPPORTING_DOCUMENTS: {
      return {
        ...state,
        supportingDocuments: action.payload.supportingDocuments,
      };
    }
    case PayeeActionTypes.SET_CLIENT_DETAILS_ARENA: {
      return {
        ...state,
        arenaName: action.payload.arenaName,
        arenaId: action.payload.arenaId,
      };
    }
    case PayeeActionTypes.SET_TAX_DOCUMENTS: {
      return {
        ...state,
        taxDocuments: action.payload.taxDocuments,
      };
    }
    case PayeeActionTypes.SET_PAYMENT_PREF: {
      return {
        ...state,
        paymentPref: action.payload.paymentPref,
      };
    }
    default: {
      return state;
    }
  }
};

const [useBasePayeeReducerContext, BasePayeeReducerContextProvider] =
  createReactContext<PayeeData>();

export { useBasePayeeReducerContext, BasePayeeReducerContextProvider };

const [usePayeeDispatchContext, PayeeReducerDispatchProvider] =
  createReactDispatchContext<PayeeReducerAction>();

export { usePayeeDispatchContext, PayeeReducerDispatchProvider };

export type WorkflowData = {
  stepNumber: number;
  mode: PayeeWorkflowModes;
  originalPayeeData?: PayeeData;
};

export type PayeeStepAction = {
  type: PayeeStepActionTypes;
};

export const initialWorkflowState: WorkflowData = {
  stepNumber: 0,
  mode: PayeeWorkflowModes.CREATE,
  originalPayeeData: undefined,
};

export const payeeStepReducer = (
  state: WorkflowData,
  action: PayeeStepAction,
): WorkflowData => {
  switch (action.type) {
    case PayeeStepActionTypes.INCREMENT_STEP: {
      if (state.stepNumber >= 3) {
        return state;
      }
      return {
        ...state,
        stepNumber: state.stepNumber + 1,
      };
    }
    case PayeeStepActionTypes.DECREMENT_STEP: {
      if (state.stepNumber > 0) {
        return {
          ...state,
          stepNumber: state.stepNumber - 1,
        };
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

const [usePayeeWorkflowContext, PayeeWorkflowContextProvider] =
  createReactContext<WorkflowData>();

export { usePayeeWorkflowContext, PayeeWorkflowContextProvider };

const [usePayeeWorkflowDispatchContext, PayeeWorkflowReducerDispatchProvider] =
  createReactDispatchContext<PayeeStepAction>();

export { usePayeeWorkflowDispatchContext, PayeeWorkflowReducerDispatchProvider };
